import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { supabase } from '../config/supabase';

type AdvisorRecommendation = {
  title: string;
  rationale: string;
  confidence: string;
};

type AdvisorReferencePlayer = {
  id: string;
  name: string;
};

type AdvisorReferenceMatch = {
  id: string;
  label: string;
  matchDate: string;
};

type AdvisorResponse = {
  conversationId: string;
  answer: string;
  recommendations: AdvisorRecommendation[];
  warnings: string[];
  assumptions: string[];
  referencedPlayers: AdvisorReferencePlayer[];
  referencedMatches: AdvisorReferenceMatch[];
  dataAsOf: string;
};

type ChatTurn = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  response?: AdvisorResponse;
};

type AskCourtCaptainProps = {
  open: boolean;
  onClose: () => void;
  lookups?: unknown;
  initialTeamId?: string | null;
  initialMatchEventId?: string | null;
};

const samplePrompts = [
  'When is Dylan Drive going to play again?',
  'Tell me Brian Powell’s current match schedule.',
  'When was the last time Rick and Garrett played together?',
  'What risks do you see for this match?',
];

function isAdvisorHostEnabled() {
  const flag = import.meta.env.VITE_COURTCAPTAIN_AI_ENABLED;
  if (flag && flag.toLowerCase() === 'true') return true;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === 'staging.courtcaptain.net';
}

function makeTurnId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function askCourtCaptainVisible(hasTeamAccess: boolean) {
  return hasTeamAccess && isAdvisorHostEnabled();
}

export function AskCourtCaptain({ open, onClose }: AskCourtCaptainProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
  }, [open, turns, sending]);

  const resetConversation = () => {
    setConversationId(null);
    setTurns([]);
    setError(null);
  };

  const sendMessage = async (text = message) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setMessage('');
    setError(null);
    const userTurn: ChatTurn = { id: makeTurnId(), role: 'user', text: trimmed };
    setTurns((current) => [...current, userTurn]);
    setSending(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Your login session is not available. Refresh and sign in again.');
      const response = await fetch('/api/courtcaptain-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: trimmed,
          conversationId,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || `Ask CourtCaptain failed (${response.status}).`);
      setConversationId(payload.conversationId);
      setTurns((current) => [...current, {
        id: makeTurnId(),
        role: 'assistant',
        text: payload.answer || 'CourtCaptain did not return an answer.',
        response: payload as AdvisorResponse,
      }]);
    } catch (sendError) {
      const messageText = sendError instanceof Error ? sendError.message : 'Ask CourtCaptain is unavailable right now.';
      setError(messageText);
      setTurns((current) => [...current, { id: makeTurnId(), role: 'assistant', text: messageText }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: fullScreen ? '100%' : 520,
          maxWidth: '100%',
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2,
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AutoFixHighIcon color="primary" />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Ask CourtCaptain</Typography>
              <Typography variant="body2" color="text.secondary">
                Read-only lineup, roster, availability, and match advisor.
              </Typography>
            </Box>
            <IconButton aria-label="Close Ask CourtCaptain" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" label="All accessible CourtCaptain data" color="primary" />
            <Button size="small" onClick={resetConversation}>New conversation</Button>
          </Stack>
        </Stack>

        <Divider />

        <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: 'background.default' }}>
          {turns.length === 0 ? (
            <Stack spacing={1.5}>
              <Alert severity="info">
                Ask a practical question like “when does Dylan Drive play again?”, “what is Brian Powell’s schedule?”, or “when did Rick and Garrett last play together?”
              </Alert>
              <Stack spacing={1}>
                {samplePrompts.map((prompt) => (
                  <Button
                    key={prompt}
                    variant="outlined"
                    size="small"
                    onClick={() => void sendMessage(prompt)}
                    disabled={sending}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    {prompt}
                  </Button>
                ))}
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {turns.map((turn) => (
                <Paper
                  key={turn.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    alignSelf: turn.role === 'user' ? 'flex-end' : 'stretch',
                    maxWidth: turn.role === 'user' ? '86%' : '100%',
                    bgcolor: turn.role === 'user' ? 'primary.main' : 'background.paper',
                    color: turn.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{turn.text}</Typography>
                  {turn.response && (
                    <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                      {turn.response.recommendations.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Recommendations</Typography>
                          <Stack spacing={0.75}>
                            {turn.response.recommendations.map((recommendation, index) => (
                              <Box key={`${recommendation.title}-${index}`}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{recommendation.title}</Typography>
                                <Typography variant="caption" color="text.secondary">{recommendation.rationale}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {turn.response.warnings.length > 0 && (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          {turn.response.warnings.join(' ')}
                        </Alert>
                      )}
                      {turn.response.assumptions.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Assumptions: {turn.response.assumptions.join(' ')}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Data as of {new Date(turn.response.dataAsOf).toLocaleString()}
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              ))}
              {sending && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">CourtCaptain is thinking...</Typography>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </Box>

        <Divider />
        {error && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about schedules, pair history, lineup risks..."
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void sendMessage();
                }
              }}
              multiline
              maxRows={4}
              disabled={sending}
            />
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={() => void sendMessage()}
              disabled={!message.trim() || sending}
            >
              Send
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}
